import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import * as _ from 'lodash-es';

@Injectable({
  providedIn: 'root'
})
export class SettlementMainService {

  constructor(private http: HttpClient) { }

  /**
   * Fetch modal data for settlement
   * Matches AngularJS: settlementMainFactory.fetchModalData
   * action_code: gc8tbu (GET)
   */
  fetchModalData(
    baseUrl: string,
    rowData: any,
    ssoId: string,
    globalParams: any = {},
    filterParams: any = {}
  ): Observable<any> {
    const url = `${baseUrl}/loadJsonObject?action_code=gc8tbu`;

    const params: any = {
      sso_id: ssoId,
      tax_year: rowData.TAX_YEAR,
      scenario: globalParams.scenario || '',
      jcd_key: globalParams.jcd_key || '',
      combination_key: rowData.COMBINATION_KEY,
      jcd_ta_key: null,
      issue_key: rowData.ISSUE_KEY,
      form_no: 'F_SETTLEMENT'
    };

    let httpParams = new HttpParams();
    Object.keys(params).forEach(key => {
      if (params[key] !== undefined && params[key] !== null) {
        httpParams = httpParams.set(key, params[key].toString());
      }
    });

    return this.http.get<any>(url, { params: httpParams, withCredentials: true });
  }

  /**
   * Get ADN/BUC codes
   * Matches AngularJS: settlementMainFactory.getAdnBucCodes
   * action_code: yieemt (GET)
   */
  getAdnBucCodes(
    baseUrl: string,
    ssoId: string,
    filterParams: any = {}
  ): Observable<any> {
    const url = `${baseUrl}/loadJsonObject?action_code=yieemt`;

    const params: any = { ...filterParams };
    params.sso_id = ssoId;

    let httpParams = new HttpParams();
    Object.keys(params).forEach(key => {
      if (params[key] !== undefined && params[key] !== null) {
        httpParams = httpParams.set(key, params[key].toString());
      }
    });

    return this.http.get<any>(url, { params: httpParams, withCredentials: true });
  }

  /**
   * Get settlement types
   * Matches AngularJS: settlementMainFactory.getSettlementTypes
   * action_code: ho2jiz (GET)
   */
  getSettlementTypes(
    baseUrl: string,
    ssoId: string,
    filterParams: any = {}
  ): Observable<any> {
    const url = `${baseUrl}/loadJsonObject?action_code=ho2jiz`;

    const params: any = { ...filterParams };
    params.sso_id = ssoId;

    let httpParams = new HttpParams();
    Object.keys(params).forEach(key => {
      if (params[key] !== undefined && params[key] !== null) {
        httpParams = httpParams.set(key, params[key].toString());
      }
    });

    return this.http.get<any>(url, { params: httpParams, withCredentials: true });
  }

  /**
   * Save modal data
   * Matches AngularJS: settlementMainFactory.saveModal
   * action_code: 2aymat (POST)
   */
  saveModal(
    baseUrl: string,
    finalData: any[],
    clobSettings: any[],
    globalParams: any = {},
    filterParams: any = {}
  ): Observable<any> {
    const url = `${baseUrl}/saveJsonObject?action_code=2aymat`;

    const clobSettingStr = JSON.stringify(clobSettings);
    const clobDataStr = JSON.stringify(finalData);

    let params: any = { ...filterParams };
    params.tax_year = globalParams.tax_year || '';
    params.scenario = globalParams.scenario || '';
    params.jcd_key = globalParams.jcd_key || '';
    params.form_no = 'F_SETTLEMENT';
    params = _.merge({}, globalParams, params);
    params.clob_data = clobDataStr;
    params.clob_settings = clobSettingStr;
    params.process_name = 'settlementMain-consol-amounts-save';

    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/x-www-form-urlencoded'
      }),
      withCredentials: true
    };

    const formData = new URLSearchParams();
    Object.keys(params).forEach(key => {
      if (params[key] !== undefined && params[key] !== null) {
        formData.set(key, params[key].toString());
      }
    });

    return this.http.post<any>(url, formData.toString(), httpOptions);
  }

  /**
   * Save sign-off
   * Matches AngularJS: settlementMainFactory.saveSignOff
   * action_code: u9d7ls (POST)
   */
  saveSignOff(
    baseUrl: string,
    rowData: any,
    globalParams: any = {}
  ): Observable<any> {
    const url = `${baseUrl}/saveJsonObject?action_code=u9d7ls`;

    const clobData = [rowData];
    const clobSetting = [{ tax_year: globalParams.tax_year || '' }];

    let params: any = {};
    params.scenario = globalParams.scenario || '';
    params.process_name = 'Settlement-save-signoff';
    params.tax_year = globalParams.tax_year || '';
    params.form_no = 'F_SETTLEMENT';
    params.is_issue_key_enabled = 'N';
    params.clob_data = JSON.stringify(clobData);
    params.clob_settings = JSON.stringify(clobSetting);

    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/x-www-form-urlencoded'
      }),
      withCredentials: true
    };

    const formData = new URLSearchParams();
    Object.keys(params).forEach(key => {
      if (params[key] !== undefined && params[key] !== null) {
        formData.set(key, params[key].toString());
      }
    });

    return this.http.post<any>(url, formData.toString(), httpOptions);
  }

  /**
   * BU Sign-off
   * Matches AngularJS: settlementMainFactory.BUSignOff
   * action_code: s6dauo (POST)
   */
  buSignOff(
    baseUrl: string,
    rowData: any,
    globalParams: any = {}
  ): Observable<any> {
    const url = `${baseUrl}/saveJsonObject?action_code=s6dauo`;

    const clobData = [rowData];
    const clobSetting = [{ tax_year: globalParams.tax_year || '' }];

    let params: any = {};
    params.scenario = globalParams.scenario || '';
    params.process_name = 'Settlement-BU-signoff';
    params.tax_year = globalParams.tax_year || '';
    params.form_name = 'Tax Due Summary by HO';
    params.is_issue_key_enabled = 'N';
    params.jcd_key = globalParams.jcd_key || '';
    params.clob_data = JSON.stringify(clobData);
    params.clob_settings = JSON.stringify(clobSetting);

    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/x-www-form-urlencoded'
      }),
      withCredentials: true
    };

    const formData = new URLSearchParams();
    Object.keys(params).forEach(key => {
      if (params[key] !== undefined && params[key] !== null) {
        formData.set(key, params[key].toString());
      }
    });

    return this.http.post<any>(url, formData.toString(), httpOptions);
  }
}
